import { createFileRoute } from '@tanstack/react-router';
// Half a 72rem row on desktop, so this one runs wider than the card photos.
import foundationsImg from 'assets/foundations.webp?w=600;1000&format=webp&quality=70&as=picture';
import { COMPANY } from 'features/Landing/content';
import { Photo } from 'features/Landing/Photo';
import { Container, CtaBand, TitleBlock } from 'features/Landing/primitives';
import { StatsBand } from 'features/Landing/sections/StatsBand';
import { Testimonials } from 'features/Landing/sections/Testimonials';
import { type FC } from 'react';
import { breadcrumbJsonLd, buildHead } from 'seo/head';

/** Half the 72rem row above `lg`, full width below it. */
const STORY_PHOTO_SIZES =
  '(min-width: 1152px) 520px, (min-width: 1024px) calc(50vw - 56px), (min-width: 640px) calc(100vw - 64px), calc(100vw - 40px)';

const VALUES = [
  {
    title: 'Quality',
    body: 'No shortcuts, ever. Every floor is poured to the drawings and finished like it’s our own place.'
  },
  {
    title: 'Straight talk',
    body: 'Fixed quotes, honest timeframes, and a phone that gets answered. If something changes, you hear it from us first.'
  },
  {
    title: 'Reliability',
    body: 'Builders plan whole programmes around our pour dates. We show up, and the floor goes down when we said it would.'
  },
  {
    title: 'Better ways to build',
    body: 'From pod slab systems to our own job-sheet software, we keep finding faster, more accurate ways to get concrete right.'
  }
] as const;

const AboutPage: FC = () => (
  <>
    <section className="bg-ink-900 pt-32 pb-16 sm:pt-40 sm:pb-20">
      <div className="mx-auto w-full max-w-6xl px-5 sm:px-8">
        <p className="font-mono text-xs tracking-[0.25em] text-brand-400 uppercase">
          About - since {COMPANY.foundingYear}
        </p>
        <h1 className="font-display mt-4 max-w-3xl text-4xl leading-tight font-bold text-white sm:text-5xl">
          Waikato&rsquo;s trusted concrete specialists
        </h1>
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-ink-300 sm:text-lg">
          A family-run crew that&rsquo;s poured more than 600 floors across Hamilton, Cambridge,
          Morrinsville and Te Awamutu, for the region&rsquo;s biggest builders and its backyards.
        </p>
      </div>
    </section>

    <section className="py-20 sm:py-28">
      <Container>
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <TitleBlock
              sheet="DB-01 - Our story"
              title="Started with one mixer and a standard"
            />
            <div className="space-y-4 text-base leading-relaxed text-ink-700">
              <p>
                Dreams Built started in {COMPANY.foundingYear} with a simple frustration: too much
                concrete in the Waikato was going down late, out of level, or not to the drawings.
                We thought the bar should be higher, so we set it there.
              </p>
              <p>
                Ten years on, we pour the foundations under homes for {`Waikato's`} group builders,
                alongside the driveways, patios and repairs that keep homeowners recommending us to their
                neighbours.
              </p>
              <p>
                We&rsquo;re still family-run, still local, and still measuring every job in
                millimetres. The day that stops mattering to us is the day we stop pouring.
              </p>
            </div>
          </div>
          <div className="overflow-hidden rounded-lg border border-ink-200">
            <Photo
              photo={foundationsImg}
              alt="Dreams Built crew boxing a residential foundation"
              sizes={STORY_PHOTO_SIZES}
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      </Container>
    </section>

    <StatsBand />

    <section className="border-b border-ink-200 bg-white py-20 sm:py-28">
      <Container>
        <TitleBlock sheet="DB-02 - How we work" title="What we hold every job to" />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {VALUES.map((value) => (
            <div key={value.title} className="rounded-lg border border-ink-200 p-6">
              <h3 className="font-display text-lg font-bold text-ink-900">{value.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-ink-600">{value.body}</p>
            </div>
          ))}
        </div>
      </Container>
    </section>

    <Testimonials />
    <CtaBand title="Want your floor to be next?" />
  </>
);

export const Route = createFileRoute('/_landing/about')({
  head: () =>
    buildHead({
      title: "About Dreams Built | Waikato's Concrete Foundation Specialists",
      description:
        'Family-run concrete specialists pouring foundations, driveways and patios across the Waikato since 2015. 600+ floors for group builders and homeowners. Meet Dreams Built.',
      path: '/about',
      jsonLd: [
        breadcrumbJsonLd([
          { name: 'Home', path: '/' },
          { name: 'About', path: '/about' }
        ])
      ]
    }),
  component: AboutPage
});
